import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import type {
  Components as VirtuosoComponents,
  ItemProps,
  ListProps,
} from 'react-virtuoso'
import { Virtuoso } from 'react-virtuoso'

import useKeyboardShortcuts, {
  defaultWithInputOnScreen,
} from '@src/lib/hooks/useKeyboardShortcuts'

import * as styles from './KeyboardList.css'
import type { KeyboardListItem } from './KeyboardListController'
import type KeyboardListController from './KeyboardListController'

const overscan = { main: 1000, reverse: 1000 }

interface KeyboardListProps<T extends KeyboardListItem> {
  node?: Element | Document
  controller: KeyboardListController<T>
  className?: string
  renderItem(item: T, index: number): React.ReactNode
  allowedShortcutsAlways?: string[]
  allowedShortcutsWhenEmpty?: string[]
  pointerEvents?: 'auto' | 'none'
  showScrollBar?: boolean
}

const computeItemKey = (index: number, item: KeyboardListItem): string => item.id

interface Context<T extends KeyboardListItem> {
  controller: KeyboardListController<T>
}

type Components<T extends KeyboardListItem> = VirtuosoComponents<Context<T>>

const Scroller = forwardRef(
  (
    // FIXME: making components generic isn't possible w/ forwardRef
    { children, context, ...props }: any,
    outerRef: React.ForwardedRef<HTMLDivElement>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- FIXME: Fix this ESLint violation!
    const { controller } = context

    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- FIXME: Fix this ESLint violation!
      controller.updateHighlighter(false)
    }, [controller])

    return (
      <div {...props} ref={outerRef}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation! */}
        <div ref={controller.highlighterRef} className={styles.highlighter} />
        {children}
      </div>
    )
  },
)

const List = forwardRef(function <T extends KeyboardListItem>(
  { context: _context, ...props }: ListProps & { context?: Context<T> },
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  return <div {...props} ref={ref} role="list" />
  // FIXME: making components generic isn't possible w/ forwardRef
}) as React.ForwardRefExoticComponent<any>

const Item = forwardRef(function <T extends KeyboardListItem>(
  { context, ...itemProps }: ItemProps<T> & { context?: Context<T> },
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const index = itemProps['data-item-index']
  const item = context?.controller.data[index]

  return (
    <div
      {...itemProps}
      ref={ref}
      role="listitem"
      onMouseEnter={
        context?.controller.itemHoverMode === 'select'
          ? () => {
              if (item) {
                context?.controller.handleItemMouseEnter(item, index)
              }
            }
          : undefined
      }
      onFocus={
        context?.controller.itemFocusMode === 'select'
          ? () => {
              if (item) {
                context.controller.handleItemMouseEnter(item, index)
              }
            }
          : undefined
      }
    />
  )
  // FIXME: making components generic isn't possible w/ forwardRef
}) as React.ForwardRefExoticComponent<any>

const Header = function <T extends KeyboardListItem>({
  context,
  ...props
}: {
  context?: Context<T>
}) {
  const height = context?.controller.listPadding.top ?? 0
  return height > 0 ? <div {...props} style={{ height }} /> : null
}

const Footer = function <T extends KeyboardListItem>({
  context,
  ...props
}: {
  context?: Context<T>
}) {
  const height = context?.controller.listPadding.bottom ?? 0
  return height > 0 ? <div {...props} style={{ height }} /> : null
}

const KeyboardList: <T extends KeyboardListItem>(
  props: KeyboardListProps<T>,
) => React.ReactElement<KeyboardListProps<T>> = function ({
  node = document,
  controller,
  className,
  allowedShortcutsAlways,
  allowedShortcutsWhenEmpty,
  renderItem,
  pointerEvents: pointerEventsProp = 'auto',
  showScrollBar,
}) {
  const [pointerEvents, setPointerEvents] = useState(pointerEventsProp)

  useEffect(() => {
    setPointerEvents(pointerEventsProp)
  }, [pointerEventsProp])

  useKeyboardShortcuts({
    name: 'keyboard-list',
    node,
    filter: defaultWithInputOnScreen({
      disallowedShortcuts: controller.disallowedShortcuts,
      allowedShortcutsAlways: allowedShortcutsAlways ?? ['Enter', 'Escape'],
      allowedShortcutsWhenEmpty: allowedShortcutsWhenEmpty ?? [
        'ArrowDown',
        'ArrowUp',
        'Meta+ArrowUp',
        'Meta+ArrowDown',
      ],
    }),
    handler: (shortcut, event) => {
      const index = controller.stepper.index

      if (controller.data.length === 0) {
        return
      }

      const currentKeyBoardListControllerData = controller.data[index]

      if (shortcut === 'Enter') {
        // @ts-expect-error unchecked index access
        return controller.handleEnter?.(currentKeyBoardListControllerData, index, event)
      }

      if (shortcut === 'Escape' && controller.handleEscape) {
        event.preventDefault()
        // @ts-expect-error unchecked index access
        return controller.handleEscape(currentKeyBoardListControllerData, index, event)
      }

      const stepperHandlersByShortcuts: Record<string, () => void> = {
        ArrowUp: () => controller.stepper.prev(),
        'Meta+ArrowUp': () => controller.stepper.start(),
        ArrowDown: () => controller.stepper.next(),
        'Meta+ArrowDown': () => controller.stepper.end(),
      }

      const matchedStepperHandler = stepperHandlersByShortcuts[shortcut]

      if (!matchedStepperHandler) {
        return
      }

      event.preventDefault()
      setPointerEvents('none')
      controller.handleRef.current?.scrollIntoView({ index })
      matchedStepperHandler()
    },
    dep: [
      controller,
      node,
      allowedShortcutsWhenEmpty,
      allowedShortcutsAlways,
      controller.disallowedShortcuts,
    ],
  })

  const itemContent = useCallback(
    (index: number, item: (typeof controller.data)[number]) => renderItem(item, index),
    [controller, renderItem],
  )

  const context: Context<(typeof controller.data)[number]> = useMemo(
    () => ({ controller }),
    [controller],
  )

  const components = useMemo(
    () => ({
      Scroller,
      List,
      Header,
      Footer,
      Item,
    }),
    [],
  )

  return (
    <div
      tabIndex={-1}
      className={cx(styles.root, className)}
      style={{ height: controller.viewportHeight }}
      onMouseMove={() => {
        if (pointerEventsProp === 'auto') {
          setPointerEvents('auto')
        }
        controller.handleMouseMove()
      }}
      onWheel={() => {
        if (pointerEventsProp === 'auto') {
          setPointerEvents('auto')
        }
      }}
    >
      <Virtuoso
        style={{ pointerEvents }}
        ref={controller.handleRef}
        scrollerRef={controller.setScrollerElement}
        context={context}
        className={showScrollBar ? '' : styles.noScrollBar}
        data={controller.data}
        overscan={overscan}
        itemContent={itemContent}
        components={components}
        computeItemKey={computeItemKey}
        initialTopMostItemIndex={controller.initialTopMostItemIndex}
        fixedItemHeight={controller.fixedItemHeight}
        endReached={controller.handleLoadMoreThresholdReached}
        totalListHeightChanged={controller.handleListHeightChanged}
        itemsRendered={controller.handleItemsRendered}
        tabIndex={-1}
      />
    </div>
  )
}

export default observer(KeyboardList)
